import { FeedbackLine } from "../ui/FeedbackLine";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, Box, Checkbox, Group, Paper, Table, Text, UnstyledButton } from "@mantine/core";
import { useApplyRun, useCancelRun, useCreateRun, useMaintenanceRun } from "../api/maintenance";
import { ApiError } from "../api/client";
import type {
  MaintenanceBucket as MaintenanceBucketT,
  MaintenanceRun,
  MaintenanceWeek as MaintenanceWeekT,
} from "../api/types";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import {
  applyBlockedReason,
  captionSignature,
  summarise,
  weekHeights,
  weekWindow,
} from "./reparse";
import { Chip } from "../ui/Chip";
import { FieldDiff } from "../ui/FieldDiff";
import { StatePill } from "../ui/StatePill";

/** Run states that will not change again, so nothing is waiting on them. */
export const FINISHED = new Set(["succeeded", "failed", "cancelled"]);

/**
 * The monospace stack this screen has always used, kept verbatim.
 *
 * Not `ff="monospace"`: that resolves to `--mantine-font-family-monospace`, a different set
 * of faces, so a signature and the fingerprint beside it would stop matching.
 */
export const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

/** The empty tick set a fresh run is read against, shared so no render allocates one. */
const NO_EXCLUSIONS: ReadonlySet<string> = new Set();

export function RunDetail({ runId }: { runId: number }) {
  const { data: run } = useMaintenanceRun(runId);
  const apply = useApplyRun();
  const cancel = useCancelRun();
  const create = useCreateRun();
  const [error, setError] = useState<string | null>(null);
  // A different plan is a different set of shapes; carrying a tick across would silently
  // exclude something nobody looked at -- so the ticks are tagged with the run they were made
  // against, and a different run is read as none of them ticked.
  const [ticked, setTicked] = useState<{ runId: number; signatures: ReadonlySet<string> } | null>(
    null,
  );
  const excluded = ticked?.runId === runId ? ticked.signatures : NO_EXCLUSIONS;
  const { t } = useTranslation("admin");

  if (!run) return null;

  const blockedReason = applyBlockedReason(run, t);
  const notes = (run.report?.notes as string[] | undefined) ?? [];

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  function toggle(signature: string) {
    setTicked((current) => {
      const next = new Set(current?.runId === runId ? current.signatures : []);
      if (!next.delete(signature)) next.add(signature);
      return { runId, signatures: next };
    });
  }

  /**
   * Re-plan without the ticked shapes, or scoped to one week.
   *
   * A new run every time, never an edit of this one. A bucket is a filter over which rows a
   * repair looks at, decided before anything is parsed -- it is not a way to commit half of
   * a plan that already exists. The parse decides several fields together (city, region and
   * country come out of one split, and the schedule fills region and country strictly as a
   * pair), so committing part of a row's change would leave a combination no parse could
   * produce and no later repair could put right.
   */
  async function replan(params: Record<string, unknown>, kind?: string) {
    await act(async () => {
      await create.mutateAsync({ kind: kind ?? run!.kind, params });
    });
  }

  return (
    <Paper component="section" mt={18} mb={26} p={14} maw={900}>
      <Group component="header" justify="space-between" gap={16}>
        <div>
          <Text component="span" size="sm">
            {run.label}
          </Text>
          <Text component="span" size="xs" c="dimmed" ml={8}>
            {t("maintenancePage.runId", { id: run.id })}
          </Text>
        </div>
        <RunState run={run} />
      </Group>

      <Text fz={13} mt={10}>
        {summarise(run, t)}
      </Text>

      {/* A marker change that re-cuts shows already in the archive is not a detail, so a
          note the server marked WARNING is coloured rather than dimmed like the rest. */}
      {notes.map((note, index) => (
        <Text key={index} fz={12} mt={6} c={note.startsWith("WARNING") ? "warn" : "dimmed"}>
          {note}
        </Text>
      ))}

      <FeedbackLine role="alert">{run.error}</FeedbackLine>
      {blockedReason && (
        <Text fz={12} mt={6} c="dimmed">
          {blockedReason}
        </Text>
      )}

      {run.weeks.length > 1 && (
        <EraStrip weeks={run.weeks} onPick={(window) => void replan(window)} />
      )}

      {run.buckets.length > 0 && (
        <Buckets buckets={run.buckets} excluded={excluded} onToggle={toggle} />
      )}

      {excluded.size > 0 && (
        <Text fz={12} mt={6} c="dimmed">
          {t("maintenancePage.excludingNote", { count: excluded.size })}
        </Text>
      )}

      <Group gap={6} mt={12}>
        {excluded.size > 0 && (
          <Chip
            disabled={create.isPending}
            onClick={() => void replan({ ...run.params, exclude_signatures: [...excluded] })}
          >
            {create.isPending ? t("maintenancePage.previewing") : t("maintenancePage.previewAgain")}
          </Chip>
        )}
        {run.state === "planned" && (
          <Chip
            variant="accent"
            disabled={blockedReason !== null || apply.isPending}
            onClick={() => void act(() => apply.mutateAsync(run.id))}
          >
            {apply.isPending
              ? t("maintenancePage.applying")
              : t("maintenancePage.applyChanges", { count: run.planned_count })}
          </Chip>
        )}
        {!FINISHED.has(run.state) && (
          <Chip
            variant="danger"
            disabled={cancel.isPending}
            onClick={() => void act(() => cancel.mutateAsync(run.id))}
          >
            {t("common.cancel")}
          </Chip>
        )}
        {run.undoable && (
          <Chip
            disabled={create.isPending}
            title={t("maintenancePage.undoTitle")}
            onClick={() => void replan({ target_run_id: run.id }, "undo")}
          >
            {t("maintenancePage.undoRepair")}
          </Chip>
        )}
      </Group>

      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Paper>
  );
}

/**
 * The shapes of change in a plan, each with a few real rows under it.
 *
 * Grouping is the whole point. Twelve thousand rows is not a thing anybody can approve; five
 * statements about what would happen to them is. The samples are there so a shape can be
 * checked rather than taken on trust — "country filled in" is only reassuring once you have
 * seen that the country it filled in is the right one.
 */
function Buckets({
  buckets,
  excluded,
  onToggle,
}: {
  buckets: MaintenanceBucketT[];
  excluded: ReadonlySet<string>;
  onToggle: (signature: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(buckets[0]?.signature ?? null);
  const { t } = useTranslation("admin");

  return (
    <Accordion
      value={open}
      onChange={setOpen}
      mt={14}
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      {buckets.map((bucket) => {
        const isExcluded = excluded.has(bucket.signature);
        return (
          <Accordion.Item
            key={bucket.signature}
            value={bucket.signature}
            bg="var(--mantine-color-default)"
            opacity={isExcluded ? 0.5 : undefined}
            style={{
              border: "1px solid var(--mantine-color-default-border)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Group gap={12} pr={12} pb={8}>
              <Accordion.Control style={{ flex: "1 1 220px", minWidth: 0 }}>
                <Group gap={10} align="baseline" wrap="nowrap">
                  <Text
                    component="span"
                    size="sm"
                    ta="right"
                    c="accent"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      flexShrink: 0,
                      minWidth: "3.5ch",
                    }}
                  >
                    {bucket.count}
                  </Text>
                  <Text component="span" fz={13}>
                    {captionSignature(bucket.signature, t)}
                  </Text>
                </Group>
              </Accordion.Control>
              <Checkbox
                c="dimmed"
                fz={12}
                display="flex"
                style={{ alignItems: "center", flexShrink: 0 }}
                checked={isExcluded}
                onChange={() => onToggle(bucket.signature)}
                label={t("maintenancePage.leaveOut")}
              />
            </Group>

            <Accordion.Panel>
              <Box
                pt={4}
                px={12}
                pb={12}
                style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
              >
                <Text
                  component="code"
                  display="block"
                  ff={MONO}
                  fz={11}
                  c="dimmed"
                  my={8}
                  style={{ overflowX: "auto" }}
                >
                  {bucket.signature}
                </Text>
                {bucket.samples.map((sample) => (
                  <Group
                    key={sample.entity_id}
                    gap={10}
                    py={6}
                    fz={12}
                    align="stretch"
                    wrap="nowrap"
                    style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
                  >
                    <Text component="span" c="dimmed" ff={MONO} style={{ flexShrink: 0 }}>
                      #{sample.entity_id}
                    </Text>
                    <FieldDiff before={sample.before} after={sample.after} />
                  </Group>
                ))}
                {bucket.count > bucket.samples.length && (
                  <Text fz={12} mt={6} c="dimmed">
                    {t("maintenancePage.andMore", {
                      count: bucket.count - bucket.samples.length,
                    })}
                  </Text>
                )}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

/**
 * Where in the archive a repair concentrates, one bar per week.
 *
 * The bars are read off the run's own rows rather than a separate scan: those rows already are
 * the ones whose stored fields no longer match their raw title, so the weeks that stand out are
 * the weeks the station changed how it writes them. Clicking one scopes a fresh preview to that
 * week, which is how a repair gets narrowed to an era instead of swept across seven years.
 */
function EraStrip({
  weeks,
  onPick,
}: {
  weeks: MaintenanceWeekT[];
  onPick: (window: { start: string; end: string }) => void;
}) {
  const zone = useZone();
  const bars = weekHeights(weeks);
  const { t } = useTranslation("admin");

  return (
    <Box mt={14}>
      <Paper
        h={54}
        p={4}
        display="flex"
        style={{
          alignItems: "flex-end",
          gap: 2,
          overflowX: "auto",
        }}
      >
        {bars.map((bar) => (
          <UnstyledButton
            key={bar.week}
            type="button"
            display="flex"
            flex="1 0 6px"
            h="100%"
            miw={6}
            style={{ alignItems: "flex-end" }}
            title={t("maintenancePage.eraBarTitle", {
              when: msToZonedText(Date.parse(bar.week), zone.name),
              count: bar.count,
            })}
            onClick={() => onPick(weekWindow(bar.week))}
          >
            <Box
              component="span"
              display="block"
              w="100%"
              bg="accent"
              style={{ borderRadius: 2, height: `${Math.round(bar.height * 100)}%` }}
            />
          </UnstyledButton>
        ))}
      </Paper>
      <Text fz={12} mt={6} c="dimmed">
        {t("maintenancePage.eraNote")}
      </Text>
    </Box>
  );
}

function RunState({ run }: { run: MaintenanceRun }) {
  const { t } = useTranslation("admin");
  // No percentage anywhere on purpose. Progress is the state of the plan's own rows, and a
  // separate counter is exactly the thing that ends up disagreeing with them after a crash.
  if (run.state === "planned")
    return (
      <StatePill tone={run.stale ? "live" : "quiet"}>
        {run.stale ? t("maintenancePage.stateOutOfDate") : t("maintenancePage.state.planned")}
      </StatePill>
    );
  // A state this build has no word for still shows the server's own, rather than nothing.
  const working = run.state === "planning" || run.state === "applying";
  return (
    <StatePill tone={working ? "live" : "quiet"}>
      {t(`maintenancePage.state.${run.state}`, { defaultValue: run.state })}
    </StatePill>
  );
}

export function RunTable({
  runs,
  openRun,
  onOpen,
}: {
  runs: MaintenanceRun[];
  openRun: number | null;
  onOpen: (id: number) => void;
}) {
  const zone = useZone();
  const { t } = useTranslation("admin");
  if (!runs.length)
    return (
      <Text size="sm" my={14}>
        {t("maintenancePage.noRuns")}
      </Text>
    );

  return (
    <Box style={{ overflowX: "auto" }}>
      <Table miw={700}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("maintenancePage.colWhen")}</Table.Th>
            <Table.Th>{t("maintenancePage.colWhat")}</Table.Th>
            <Table.Th>{t("maintenancePage.colWho")}</Table.Th>
            <Table.Th>{t("maintenancePage.colResult")}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {runs.map((run) => (
            <Table.Tr key={run.id} bg={run.id === openRun ? "accentDim" : undefined}>
              <Table.Td>{msToZonedText(Date.parse(run.created_at), zone.name)}</Table.Td>
              <Table.Td>
                {run.label}
                {run.undo_of !== null && (
                  <Text component="span" size="xs" c="dimmed" ml={8}>
                    {t("maintenancePage.undoOfRun", { id: run.undo_of })}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                {run.requested_by ?? (
                  <Text component="span" fz="inherit" c="dimmed">
                    {t("maintenancePage.system")}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{summarise(run, t)}</Table.Td>
              <Table.Td>
                <Chip onClick={() => onOpen(run.id)}>{t("maintenancePage.open")}</Chip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
